import React, { useEffect, useState } from "react";
import AccordionComponent from "../accordion/AccordionComponent";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
} from "@material-tailwind/react";
import { useLocation, useParams } from "react-router-dom";
import FAQSchema from "../../FAQSchema";
import Http from "../../Http";
import { CARD_API } from "../../Helper";
import { Helmet } from "react-helmet";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function splitFeatures(features) {
  const midpoint = Math.ceil(features.length / 2);
  const leftSideFeatures = features.slice(0, midpoint);
  const rightSideFeatures = features.slice(midpoint);
  return { leftSideFeatures, rightSideFeatures };
}

function LearnMoreAllCards() {
  const location = useLocation();
  const { cardId } = useParams(); // Get cardId from URL params
  const [cardDetails, setCardDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Priority 1: Check if data is passed via location.state
        if (location.state) {
          console.log("Data from location.state");
          setCardDetails(location.state);
          setLoading(false);
          return;
        }

        // Priority 2: Fetch from API using cardId from URL
        if (cardId) {
          console.log("Fetching from API for cardId:", cardId);
          
          // Fetch all cards from API
          const response = await Http.get(CARD_API);
          
          console.log("API Response:", response); // Debug log
          console.log("API Response Data:", response.data); // Debug log
          
          // Create slug function to match cardId
          const createSlug = (title) => {
            return title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)/g, '');
          };

          // Handle different response structures
          let cardsArray = [];
          
          if (response.data?.cards) {
            cardsArray = response.data.cards;
          } else if (response.data?.data?.cards) {
            cardsArray = response.data.data.cards;
          } else if (Array.isArray(response.data)) {
            cardsArray = response.data;
          } else if (response.cards) {
            cardsArray = response.cards;
          } else {
            console.error("Unexpected API structure:", response);
            throw new Error("Unable to find cards in API response");
          }

          console.log("Cards Array:", cardsArray); // Debug log
          console.log("Looking for cardId:", cardId); // Debug log

          // Find the card by matching slug or id
          const foundCard = cardsArray.find(card => {
            const cardSlug = createSlug(card.title);
            console.log(`Comparing: ${cardSlug} === ${cardId}`, cardSlug === cardId); // Debug log
            return cardSlug === cardId || card.id === cardId || String(card.id) === cardId;
          });

          if (foundCard) {
            console.log("Found Card:", foundCard); // Debug log
            setCardDetails(foundCard);
            setLoading(false);
          } else {
            console.error("Card not found. Available cards:", cardsArray.map(c => ({
              id: c.id,
              title: c.title,
              slug: createSlug(c.title)
            })));
            throw new Error("Card not found in the database");
          }
        } else {
          throw new Error("No card identifier found");
        }
      } catch (err) {
        console.error("Error fetching card data:", err);
        setError(err.message || "Failed to load card details");
        setLoading(false);
      }
    };

    fetchCardData();
  }, [cardId, location.state]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-pulse">
          <div className="text-2xl font-semibold text-gray-600">Loading card details...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="text-2xl font-semibold text-red-600 mb-4">
          Error loading card details
        </div>
        <div className="text-gray-600">{error}</div>
        <button
          onClick={() => window.history.back()}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Go Back
        </button>
      </div>
    );
  }

  // No data state
  if (!cardDetails) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="text-2xl font-semibold text-gray-600 mb-4">
          Card details not found
        </div>
        <button
          onClick={() => window.history.back()}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Go Back
        </button>
      </div>
    );
  }

  const handleOpen = (value) => setOpen(open === value ? 0 : value);

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };

  // Rewards and Benefits
  const rewardsAndBenefitsFeatures =
    cardDetails.Card_features && cardDetails.Card_features[0]
      ? cardDetails.Card_features[0].subfeatures || []
      : [];
  const {
    leftSideFeatures: leftSideRewards,
    rightSideFeatures: rightSideRewards,
  } = splitFeatures(rewardsAndBenefitsFeatures);

  // Fees & Charges
  const feesAndChargesFeatures =
    cardDetails.Card_features && cardDetails.Card_features[1]
      ? cardDetails.Card_features[1].subfeatures || []
      : [];
  const { leftSideFeatures: leftSideFees, rightSideFeatures: rightSideFees } =
    splitFeatures(feesAndChargesFeatures);

  return (
    <>
    <Helmet>
        <title>
    {cardDetails.title }
  </title>

  <meta
    name="description"
    content={
      cardDetails.meta_description 
    }
  />

  <meta
    name="keywords"
    content={
      cardDetails.meta_keywords }
  />

  <link
    rel="canonical"
    href={`https://thefinmaster.com/credit-card/${cardId}`}
  />

        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={cardDetails.card_faq || []} />

      <div className="w-[100%] py-6 md:px-10 lg:px-10 sm:px-4 px-4 mt-8">
        <div className="mx-auto ml-4 border border-gray-300 rounded-md mt-4  mb-6 ">
          <div className="flex justify-between items-center px-4 py-2 mt-4 text-black">
            {/* Left Side */}
            <div className="flex items-center">
              <h1 className="text-xl font-bold">{cardDetails.title}</h1>
            </div>
            {/* Right Side */}
            <div className="flex items-center ">
              <a
                href={cardDetails.bank_link}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out"
              >
                Apply now
              </a>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Right Side - Image */}
            <div className="md:w-1/3 mb-4 md:mb-0">
              <img
                src={cardDetails.image}
                alt={cardDetails.title}
                className="w-full"
              />
              {/* Rating Component */}
              <div className="flex items-center mr-4 text-lg ml-4 mt-2">
                {Array.from(
                  { length: parseInt(cardDetails.rating) },
                  (_, i) => (
                    <svg
                      key={i}
                      className="w-4 h-4 text-yellow-300 mr-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 22 20"
                    >
                      <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                    </svg>
                  )
                )}
                <p className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {cardDetails.rating} out of 5
                </p>
              </div>
            </div>
            {/* Left Side - Headings */}
            <div className="md:w-1/3 flex flex-col justify-between mb-4 md:mb-0 ">
              <div className="mb-2 ">
                {cardDetails.Card_features &&
                  cardDetails.Card_features.map((feature) => {
                    if (feature.Title === "Fees & Charges") {
                      const fees = feature.subfeatures?.find(
                        (subfeature) => subfeature.Subfeatures === "Joining Fee"
                      );
                      return (
                        <div key={feature.Title} className="mt-4">
                          <h2 className="text-lg font-semibold mb-2 text-blue-600">
                            Fees
                          </h2>
                          <p className="text-gray-600">
                            Joining Fee: {fees ? fees.description : "Nil"}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })}
                <p className="text-gray-600">Annual Fee : {cardDetails.fees}</p>
              </div>
              <div className="mb-2">
                {cardDetails.Card_features &&
                  cardDetails.Card_features.map((feature) => {
                    if (feature.Title === "Rewards and Benefits") {
                      const welcomeBenefits = feature.subfeatures?.find(
                        (subfeature) =>
                          subfeature.Subfeatures === "Welcome Benefits"
                      );
                      return (
                        <div key={feature.Title}>
                          <h2 className="text-lg font-semibold text-blue-600">
                            Welcome Benefits
                          </h2>
                          <p className="text-gray-600">
                            {welcomeBenefits
                              ? welcomeBenefits.description
                              : "Nil"}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            </div>
            {/* Right Side - Content */}
            <div className="md:w-1/3 md:ml-6">
              <div>
                <h2 className="text-lg font-semibold mb-2 text-blue-600 ml-4">
                  Best Suited for
                </h2>
                <p className="text-gray-600 ml-4">Rewards & Cashback</p>
              </div>
              <div className="mt-4">
                {cardDetails.Card_features &&
                  cardDetails.Card_features.map((feature) => {
                    if (feature.Title === "Rewards and Benefits") {
                      const rewardRate = feature.subfeatures?.find(
                        (subfeature) =>
                          subfeature.Subfeatures === "Rewards Rate"
                      );
                      return (
                        <div key={feature.Title}>
                          <h2 className="text-lg font-semibold text-blue-600 ml-4">
                            Reward Rate
                          </h2>
                          <p className="text-gray-600 ml-4">
                            {rewardRate ? rewardRate.description : "Nil"}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  })}
              </div>
            </div>
          </div>

          {/* New Div with Border Line */}
          <div className="border-t border-gray-300 mt-4">
            {/* Content */}
            <div className="text-xl md:text-2xl lg:text-1xl font-bold  mt-3 ml-4">
              Rewards and Benefits
            </div>
            <div className="flex flex-col md:flex-row items-center py-4 ml-4">
              {/* Left Side - Headings */}
              <div className="md:w-1/2 mb-4 md:mb-0">
                {leftSideRewards.map((feature, index) => (
                  <div className="mb-2" key={index}>
                    <h2 className="text-lg font-semibold text-blue-600">
                      {" "}
                      {feature.Subfeatures}
                    </h2>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>

              {/* Right Side - Headings */}
              <div className="md:w-1/2 md:ml-6">
                {rightSideRewards.map((feature, index) => (
                  <div className="mb-2" key={index}>
                    <h2 className="text-lg font-semibold text-blue-600">
                      {feature.Subfeatures}
                    </h2>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-300 mt-4" />
          <Accordion
            open={open === 1}
            className="px-3"
            icon={<Icon id={1} open={open} />}
          >
            <AccordionHeader onClick={() => handleOpen(1)}>
              Fees & Charges
            </AccordionHeader>
            <AccordionBody>
              <div className="flex flex-col md:flex-row items-center py-4 ml-4">
                {/* Left Side - Headings */}
                <div className="md:w-1/2 mb-4 md:mb-0">
                  {leftSideFees.map((feature, index) => (
                    <div className="mb-2" key={index}>
                      <h2 className="text-lg font-semibold text-blue-600">
                        {feature.Subfeatures}
                      </h2>
                      <p className="text-gray-600 text-base">
                        {" "}
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
                {/* Right Side - Headings */}
                <div className="md:w-1/2 md:ml-6">
                  {rightSideFees.map((feature, index) => (
                    <div className="mb-2" key={index}>
                      <h2 className="text-lg font-semibold text-blue-600">
                        {feature.Subfeatures}
                      </h2>
                      <p className="text-gray-600 text-base">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionBody>
          </Accordion>
          <Accordion
            className="px-3"
            open={open === 2}
            icon={<Icon id={2} open={open} />}
          >
            <AccordionHeader onClick={() => handleOpen(2)}>
              Card Details
            </AccordionHeader>
            <AccordionBody>
              {cardDetails.Card_features &&
                cardDetails.Card_features[2] &&
                cardDetails.Card_features[2].subfeatures?.map(
                  (feature, index) => (
                    <p
                      key={index}
                      className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"
                    >
                      <span className="absolute left-0 top-0  text-blue-600">
                        &#8226;
                      </span>
                      {feature.description}
                    </p>
                  )
                )}
            </AccordionBody>
          </Accordion>
        </div>
      </div>
      <div className="text-gray-700 text-base mb-4 md:px-12 lg:px-12 sm:px-4 px-4 ">
        {cardDetails.Card_details ? (
          <p dangerouslySetInnerHTML={{ __html: cardDetails.Card_details }}></p>
        ) : (
          <p>No additional details available.</p>
        )}
      </div>
      <div className="max-w-4xl mx-auto py-4 px-2">
        <h2 className="text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {cardDetails.card_faq && cardDetails.card_faq.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <svg
                  className={`w-6 h-6 transition-transform transform ${
                    activeIndex === index ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d={
                      activeIndex === index ? "M19 9l-7 7-7-7" : "M9 5l7 7 7-7"
                    }
                  />
                </svg>
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default LearnMoreAllCards;

// import React, { useEffect, useState } from "react";
// import AccordionComponent from "../accordion/AccordionComponent";
// import {
//   Accordion,
//   AccordionHeader,
//   AccordionBody,
// } from "@material-tailwind/react";
// import { useLocation } from "react-router-dom";
// import FAQSchema from "../../FAQSchema";
// function Icon({ id, open }) {
//   return (
//     <svg
//       xmlns="http://www.w3.org/2000/svg"
//       fill="none"
//       viewBox="0 0 24 24"
//       strokeWidth={2}
//       stroke="currentColor"
//       className={`${
//         id === open ? "rotate-180" : ""
//       } h-5 w-5 transition-transform`}
//     >
//       <path
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         d="M19.5 8.25l-7.5 7.5-7.5-7.5"
//       />
//     </svg>
//   );
// }
// function splitFeatures(features) {
//   const midpoint = Math.ceil(features.length / 2);
//   const leftSideFeatures = features.slice(0, midpoint);
//   const rightSideFeatures = features.slice(midpoint);
//   return { leftSideFeatures, rightSideFeatures };
// }

// function LearnMoreAllCards() {
//   //    const location = useLocation();
//   //    const cardDetails =location.state;
//   //    console.log(cardDetails, location.state)

//   const location = useLocation();
//   const [cardDetails, setCardDetails] = useState(null);
//   const [open, setOpen] = React.useState(0);
//   const [activeIndex, setActiveIndex] = useState(0);
//   useEffect(() => {
//     if (location.state) {
//       // Save to local storage
//       localStorage.setItem("cardDetails", JSON.stringify(location.state));
//       setCardDetails(location.state);
//     } else {
//       // Retrieve from local storage
//       const savedCardDetails = localStorage.getItem("cardDetails");
//       if (savedCardDetails) {
//         setCardDetails(JSON.parse(savedCardDetails));
//       }
//     }
//   }, [location.state]);

//   if (!cardDetails) {
//     return <div>Loading...</div>;
//   }
//   const handleOpen = (value) => setOpen(open === value ? 0 : value);

//   const faqs = [
//     {
//       question:
//         "When can I utilize the accumulated reward points earned with the AU Small Finance Bank LIT Credit Card?",
//       answer:
//         "You can redeem the earned reward points through the AU Small Finance Bank LIT Credit Card after the statement is generated",
//     },
//   ];
//   const toggleAccordion = (index) => {
//     if (activeIndex === index) {
//       setActiveIndex(null);
//     } else {
//       setActiveIndex(index);
//     }
//   };
//   // Dynamically calculate the split point for features
//   // const features = cardDetails.Card_features && cardDetails.Card_features[0] ? cardDetails.Card_features[0].subfeatures : [];
//   // const midpoint = Math.ceil(features.length / 2);
//   // const leftSideFeatures = features.slice(0, midpoint);
//   // const rightSideFeatures = features.slice(midpoint);

//   // Rewards and Benefits
//   const rewardsAndBenefitsFeatures =
//     cardDetails.Card_features && cardDetails.Card_features[0]
//       ? cardDetails.Card_features[0].subfeatures
//       : [];
//   const {
//     leftSideFeatures: leftSideRewards,
//     rightSideFeatures: rightSideRewards,
//   } = splitFeatures(rewardsAndBenefitsFeatures);

//   // Fees & Charges
//   const feesAndChargesFeatures =
//     cardDetails.Card_features && cardDetails.Card_features[1]
//       ? cardDetails.Card_features[1].subfeatures
//       : [];
//   const { leftSideFeatures: leftSideFees, rightSideFeatures: rightSideFees } =
//     splitFeatures(feesAndChargesFeatures);

//   return (
//     <>
//       <FAQSchema faqs={cardDetails.card_faq} />

//       <div className="w-[100%] py-6 md:px-10 lg:px-10 sm:px-4 px-4 mt-8">
//         <div className="mx-auto ml-4 border border-gray-300 rounded-md mt-4  mb-6 ">
//           <div className="flex justify-between items-center px-4 py-2 mt-4 text-black">
//             {/* Left Side */}
//             <div className="flex items-center">
//               <h1 className="text-xl font-bold">{cardDetails.title}</h1>
//             </div>
//             {/* Right Side */}
//             <div className="flex items-center ">
//               {/* Replace 'your_link_here' with your actual link */}
//               <a
//                 href={cardDetails.bank_link}
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="ml-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out"
//               >
//                 Apply now
//               </a>
//             </div>
//           </div>
//           <div className="flex flex-col md:flex-row items-center gap-6">
//             {/* Right Side - Image */}
//             <div className="md:w-1/3 mb-4 md:mb-0">
//               <img
//                 src={cardDetails.image}
//                 alt={cardDetails.title}
//                 className="w-full"
//               />
//               {/* Rating Component */}
//               <div className="flex items-center mr-4 text-lg ml-4 mt-2">
//                 {/* You can replace the static stars with your actual rating logic */}
//                 {Array.from(
//                   { length: parseInt(cardDetails.rating) },
//                   (_, i) => (
//                     <svg
//                       key={i}
//                       className="w-4 h-4 text-yellow-300 mr-1"
//                       aria-hidden="true"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="currentColor"
//                       viewBox="0 0 22 20"
//                     >
//                       <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
//                     </svg>
//                   )
//                 )}
//                 <p className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">
//                   {cardDetails.rating} out of 5
//                 </p>
//               </div>
//             </div>
//             {/* Left Side - Headings */}
//             <div className="md:w-1/3 flex flex-col justify-between mb-4 md:mb-0 ">
//               <div className="mb-2 ">
//                 {/* <h2 className="text-lg font-semibold text-blue-600">Fees</h2> */}
//                 {/* <p className='text-gray-600'>Joining Fee: Nil</p> */}{" "}
//                 {cardDetails.Card_features &&
//                   cardDetails.Card_features.map((feature) => {
//                     if (feature.Title === "Fees & Charges") {
//                       const fees = feature.subfeatures.find(
//                         (subfeature) => subfeature.Subfeatures === "Joining Fee"
//                       );
//                       return (
//                         <div key={feature.Title} className="mt-4">
//                           <h2 className="text-lg font-semibold mb-2 text-blue-600">
//                             Fees
//                           </h2>
//                           <p className="text-gray-600">
//                             Joining Fee: {fees ? fees.description : "Nil"}
//                           </p>
//                         </div>
//                       );
//                     }
//                     return null;
//                   })}
//                 <p className="text-gray-600">Annual Fee : {cardDetails.fees}</p>
//               </div>
//               <div className="mb-2">
//                 {cardDetails.Card_features &&
//                   cardDetails.Card_features.map((feature) => {
//                     if (feature.Title === "Rewards and Benefits") {
//                       const welcomeBenefits = feature.subfeatures.find(
//                         (subfeature) =>
//                           subfeature.Subfeatures === "Welcome Benefits"
//                       );
//                       return (
//                         <div key={feature.Title}>
//                           <h2 className="text-lg font-semibold text-blue-600">
//                             Welcome Benefits
//                           </h2>
//                           <p className="text-gray-600">
//                             {welcomeBenefits
//                               ? welcomeBenefits.description
//                               : "Nil"}
//                           </p>
//                         </div>
//                       );
//                     }
//                     return null;
//                   })}
//               </div>
//             </div>
//             {/* Right Side - Content */}
//             <div className="md:w-1/3 md:ml-6">
//               <div>
//                 <h2 className="text-lg font-semibold mb-2 text-blue-600 ml-4">
//                   Best Suited for
//                 </h2>
//                 <p className="text-gray-600 ml-4">Rewards & Cashback</p>
//               </div>
//               <div className="mt-4">
//                 {/* <h2 className="text-lg font-semibold mb-2 text-blue-600">Reward Rate</h2>
//                                 <p className='text-gray-600'>0.25%</p> */}
//                 {cardDetails.Card_features &&
//                   cardDetails.Card_features.map((feature) => {
//                     if (feature.Title === "Rewards and Benefits") {
//                       const rewardRate = feature.subfeatures.find(
//                         (subfeature) =>
//                           subfeature.Subfeatures === "Rewards Rate"
//                       );
//                       return (
//                         <div key={feature.Title}>
//                           <h2 className="text-lg font-semibold text-blue-600 ml-4">
//                             Reward Rate
//                           </h2>
//                           <p className="text-gray-600 ml-4">
//                             {rewardRate ? rewardRate.description : "Nil"}
//                           </p>
//                         </div>
//                       );
//                     }
//                     return null;
//                   })}
//               </div>
//             </div>
//           </div>

//           {/* New Div with Border Line */}
//           <div className="border-t border-gray-300 mt-4">
//             {/* Content */}
//             <div className="text-xl md:text-2xl lg:text-1xl font-bold  mt-3 ml-4">
//               Rewards and Benefits
//             </div>
//             <div className="flex flex-col md:flex-row items-center py-4 ml-4">
//               {/* Left Side - Headings */}
//               <div className="md:w-1/2 mb-4 md:mb-0">
//                 {leftSideRewards.map((feature, index) => (
//                   <div className="mb-2" key={index}>
//                     <h2 className="text-lg font-semibold text-blue-600">
//                       {" "}
//                       {feature.Subfeatures}
//                     </h2>
//                     <p className="text-gray-600">{feature.description}</p>
//                   </div>
//                 ))}
//                 {/* <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Travel</h2>
//                                     <p className='text-gray-600'>Enjoy free access to domestic lounges across India.
//                                         Receive 2 complimentary airport lounge accesses every quarter, totaling 8 accesses per year.</p>
//                                 </div>
//                                 <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Movie and Dining</h2>
//                                     <p className='text-gray-600'>Get 3 months of free membership for Zee5 and Amazon Prime, along with an extra 5% cashback on dining expenses.</p>
//                                 </div> */}
//               </div>

//               {/* Right Side - Headings */}
//               <div className="md:w-1/2 md:ml-6">
//                 {rightSideRewards.map((feature, index) => (
//                   <div className="mb-2" key={index}>
//                     <h2 className="text-lg font-semibold text-blue-600">
//                       {feature.Subfeatures}
//                     </h2>
//                     <p className="text-gray-600">{feature.description}</p>
//                   </div>
//                 ))}
//                 {/* <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Zero Liability Protection</h2>
//                                     <p className='text-gray-600'>The cardholder bears no liability for fraudulent transactions in case of credit card loss or theft, provided it is reported to the bank's customer care within 7 days.</p>
//                                 </div>
//                                 <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Reward Redemption</h2>
//                                     <p className='text-gray-600'>Exchange your Reward Points for fantastic discounts and offers.</p>
//                                 </div> */}
//               </div>
//             </div>
//           </div>
//           <div className="border-t border-gray-300 mt-4" />
//           <Accordion
//             open={open === 1}
//             className="px-3"
//             icon={<Icon id={1} open={open} />}
//           >
//             <AccordionHeader onClick={() => handleOpen(1)}>
//               Fees & Charges
//             </AccordionHeader>
//             <AccordionBody>
//               <div className="flex flex-col md:flex-row items-center py-4 ml-4">
//                 {/* Left Side - Headings */}
//                 <div className="md:w-1/2 mb-4 md:mb-0">
//                   {/* {cardDetails.Card_features && cardDetails.Card_features[1] && cardDetails.Card_features[1].subfeatures.slice(0, 3).map((feature, index) => ( */}
//                   {leftSideFees.map((feature, index) => (
//                     <div className="mb-2" key={index}>
//                       <h2 className="text-lg font-semibold text-blue-600">
//                         {feature.Subfeatures}
//                       </h2>
//                       <p className="text-gray-600 text-base">
//                         {" "}
//                         {feature.description}
//                       </p>
//                     </div>
//                   ))}
//                   {/* <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Rewards Redemption Fee</h2>
//                                         <p className='text-gray-600 text-base'>Nil</p>
//                                     </div>
//                                     <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Fuel Surcharge
//                                         </h2>
//                                         <p className='text-gray-600 text-base'>Enjoy a 1% waiver on fuel surcharge for transactions ranging from Rs. 400 to Rs. 5,000 at fuel stations.</p>
//                                     </div>  */}
//                 </div>
//                 {/* Right Side - Headings */}
//                 <div className="md:w-1/2 md:ml-6">
//                   {/* {cardDetails.Card_features && cardDetails.Card_features[1] && cardDetails.Card_features[1].subfeatures.slice(3, 6).map((feature, index) => ( */}
//                   {rightSideFees.map((feature, index) => (
//                     <div className="mb-2" key={index}>
//                       <h2 className="text-lg font-semibold text-blue-600">
//                         {feature.Subfeatures}
//                       </h2>
//                       <p className="text-gray-600 text-base">
//                         {feature.description}
//                       </p>
//                     </div>
//                   ))}
//                   {/* <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Waiver Based on Spending</h2>
//                                         <p className='text-gray-600 text-base' >N/A</p>
//                                     </div>
//                                     <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Add-on Card Fee</h2>
//                                         <p className='text-gray-600 text-base'>Nil</p>
//                                     </div>  */}
//                 </div>
//               </div>
//             </AccordionBody>
//           </Accordion>
//           <Accordion
//             className="px-3"
//             open={open === 2}
//             icon={<Icon id={2} open={open} />}
//           >
//             <AccordionHeader onClick={() => handleOpen(2)}>
//               Card Details
//             </AccordionHeader>
//             <AccordionBody>
//               {/* {cardDetails.Card_features && cardDetails.Card_features[2] && cardDetails.Card_features[1].subfeatures.map((feature, index) => ( */}
//               {cardDetails.Card_features &&
//                 cardDetails.Card_features[2] &&
//                 cardDetails.Card_features[2].subfeatures.map(
//                   (feature, index) => (
//                     <p
//                       key={index}
//                       className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"
//                     >
//                       <span className="absolute left-0 top-0  text-blue-600">
//                         &#8226;
//                       </span>
//                       {feature.description}
//                     </p>
//                   )
//                 )}
//               {/* <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
//                         Enjoy a complimentary 3-month membership for Zee5, Amazon Prime, and Cult.fit with your card.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
//                         Earn 1 Reward Point for every Rs. 100 spent on retail transactions.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Avail accelerated Reward Points at 10X or 5X rates for both online and offline transactions.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
//                         Benefit from a 1% surcharge waiver on fuel transactions ranging from Rs. 400 to Rs. 5,000
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> 
//                         Get 5% and 2% cashback on retail spends upon reaching the spend target.
// Receive an additional 5% cashback on travel, dining, and grocery expenses.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> 
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Enjoy 4 complimentary lounge accesses per quarter.
//                     </p> */}
//             </AccordionBody>
//           </Accordion>
//         </div>
//       </div>
//       {/* <p className="text-gray-700 text-base mb-6 px-12" dangerouslySetInnerHTML={{ __html:cardDetails.Card_details }}></p> */}
//       <div className="text-gray-700 text-base mb-4 md:px-12 lg:px-12 sm:px-4 px-4 ">
//         {cardDetails.Card_details ? (
//           <p dangerouslySetInnerHTML={{ __html: cardDetails.Card_details }}></p>
//         ) : (
//           <p>No additional details available.</p>
//         )}
//       </div>
//       <div className="max-w-4xl mx-auto py-4 px-2">
//         <h2 className="text-3xl font-semibold text-center mb-8">
//           Frequently Asked Questions
//         </h2>
//         <div className="space-y-4">
//           {cardDetails.card_faq.map((faq, index) => (
//             <div
//               key={index}
//               className="border rounded-lg overflow-hidden shadow-md"
//             >
//               <button
//                 onClick={() => toggleAccordion(index)}
//                 className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
//               >
//                 {faq.question}
//                 <svg
//                   className={`w-6 h-6 transition-transform transform ${
//                     activeIndex === index ? "rotate-180" : ""
//                   }`}
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth="2"
//                     d={
//                       activeIndex === index ? "M19 9l-7 7-7-7" : "M9 5l7 7 7-7"
//                     }
//                   />
//                 </svg>
//               </button>
//               {activeIndex === index && (
//                 <div className="p-4 bg-gray-100">
//                   <p className="text-gray-600">{faq.answer}</p>
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>
//     </>
//   );
// }

// export default LearnMoreAllCards;

// import React, { useState } from 'react';
// import AccordionComponent from '../accordion/AccordionComponent'
// import {
//     Accordion,
//     AccordionHeader,
//     AccordionBody,
// } from "@material-tailwind/react";
// import { useLocation } from 'react-router-dom';
// function Icon({ id, open }) {
//     return (
//         <svg
//             xmlns="http://www.w3.org/2000/svg"
//             fill="none"
//             viewBox="0 0 24 24"
//             strokeWidth={2}
//             stroke="currentColor"
//             className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
//         >
//             <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
//         </svg>
//     );
// }
// function LearnMoreAllCards() {
//    const location = useLocation();
//    const cardDetails =location.state;
//    console.log(cardDetails, location.state)

//     const [open, setOpen] = React.useState(0);

//     const handleOpen = (value) => setOpen(open === value ? 0 : value);

//     // Function to get current date
//     // const getCurrentDate = () => {
//     //     const date = new Date();
//     //     const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
//     //     return date.toLocaleDateString('en-US', options);
//     // };
//     const [activeIndex, setActiveIndex] = useState(null);

//     const faqs = [
//         {
//             question: 'When can I utilize the accumulated reward points earned with the AU Small Finance Bank LIT Credit Card?',
//             answer:
//             'You can redeem the earned reward points through the AU Small Finance Bank LIT Credit Card after the statement is generated',
//         },

//     ];

//     const toggleAccordion = (index) => {
//         if (activeIndex === index) {
//             setActiveIndex(null);
//         } else {
//             setActiveIndex(index);
//         }
//     };
//     // Dynamically calculate the split point for features
//     const features = cardDetails.Card_features[0].subfeatures;
//     const midpoint = Math.ceil(features.length / 2);
//     const leftSideFeatures = features.slice(0, midpoint);
//     const rightSideFeatures = features.slice(midpoint);
//     return (
//         <>

//             <div className='w-[95%] py-10 px-10'>
//                 <div className="mx-auto ml-4 border border-gray-300 rounded-md mt-4  mb-6 ">
//                     <div className="flex justify-between items-center px-4 py-2 mt-4 text-black">
//                         {/* Left Side */}
//                         <div className="flex items-center">
//                             <h1 className="text-xl font-bold">{cardDetails.title}</h1>
//                         </div>

//                         {/* Right Side */}
//                         <div className="flex items-center ">
//                             {/* Replace 'your_link_here' with your actual link */}

//                             <a href={cardDetails.bank_link}
//                             target="_blank"
//                            rel="noopener noreferrer"
//            className="ml-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out">Apply now</a>

//                         </div>

//                     </div>
//                     <div className="flex flex-col md:flex-row items-center gap-6">
//                         {/* Right Side - Image */}
//                         <div className="md:w-1/3 mb-4 md:mb-0">
//                             <img src={cardDetails.image} alt={cardDetails.title} className="w-full" />
//                             {/* Rating Component */}
//                             <div className="flex items-center mr-4 text-lg ml-4 mt-2">
//                                 {/* You can replace the static stars with your actual rating logic */}
//                                 {Array.from({ length: parseInt(cardDetails.rating) }, (_, i) => (
//                                             <svg key={i} className="w-4 h-4 text-yellow-300 mr-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 22 20">
//                                                 <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
//                                             </svg>
//                                         ))}
//                                         <p className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">{cardDetails.rating} out of 5</p>
//                             </div>
//                         </div>

//                         {/* Left Side - Headings */}
//                         <div className="md:w-1/3 flex flex-col justify-between mb-4 md:mb-0 ">
//                             <div className="mb-2 ">
//                                 <h2 className="text-lg font-semibold text-blue-600">Fees</h2>
//                                 <p className='text-gray-600'>Joining Fee: Nil</p>
//                                 <p className='text-gray-600'>Annual Fee: {cardDetails.fees}</p>
//                             </div>
//                             <div className="mb-2">
//                                 <h2 className="text-lg font-semibold text-blue-600">Welcome Benefits</h2>
//                                 <p className='text-gray-600'>Nil</p>
//                             </div>
//                         </div>

//                         {/* Right Side - Content */}
//                         <div className="md:w-1/3 md:ml-6">

//                             <div>
//                                 <h2 className="text-lg font-semibold mb-2 text-blue-600">Best Suited for</h2>
//                                 <p className='text-gray-600'>Rewards & Cashback</p>
//                             </div>
//                             <div className="mt-4">
//                                 <h2 className="text-lg font-semibold mb-2 text-blue-600">Reward Rate</h2>
//                                 <p className='text-gray-600'>0.25%</p>
//                             </div>
//                         </div>
//                     </div>
//                     {/* New Div with Border Line */}
//                     <div className="border-t border-gray-300 mt-4">
//                         {/* Content */}

//                         <div className="text-xl md:text-2xl lg:text-1xl font-bold  mt-3 ml-4">Rewards and Benefits</div>
//                         <div className="flex flex-col md:flex-row items-center py-4 ml-4">
//                             {/* Left Side - Headings */}
//                             <div className="md:w-1/2 mb-4 md:mb-0">
//                             {leftSideFeatures.map((feature, index) => (
//                                 <div className="mb-2" key={index}>
//                                     <h2 className="text-lg font-semibold text-blue-600"> {feature.Subfeatures}</h2>
//                                     <p className='text-gray-600'>{feature.description}</p>
//                                 </div>
//                                  ))}
//                                 {/* <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Travel</h2>
//                                     <p className='text-gray-600'>Enjoy free access to domestic lounges across India.
//                                         Receive 2 complimentary airport lounge accesses every quarter, totaling 8 accesses per year.</p>
//                                 </div>
//                                 <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Movie and Dining</h2>
//                                     <p className='text-gray-600'>Get 3 months of free membership for Zee5 and Amazon Prime, along with an extra 5% cashback on dining expenses.</p>
//                                 </div> */}
//                             </div>

//                             {/* Right Side - Headings */}
//                             <div className="md:w-1/2 md:ml-6">
//                             {rightSideFeatures.map((feature, index) => (
//                                 <div className="mb-2"key={index}>
//                                     <h2 className="text-lg font-semibold text-blue-600">{feature.Subfeatures}</h2>
//                                     <p className='text-gray-600'>{feature.description}</p>
//                                 </div>
//                                  ))}
//                                 {/* <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Zero Liability Protection</h2>
//                                     <p className='text-gray-600'>The cardholder bears no liability for fraudulent transactions in case of credit card loss or theft, provided it is reported to the bank's customer care within 7 days.</p>
//                                 </div>
//                                 <div className="mb-2">
//                                     <h2 className="text-lg font-semibold text-blue-600">Reward Redemption</h2>
//                                     <p className='text-gray-600'>Exchange your Reward Points for fantastic discounts and offers.</p>
//                                 </div> */}
//                             </div>
//                         </div>
//                     </div>
//                     <div className="border-t border-gray-300 mt-4" />
//                     <Accordion open={open === 1} className='px-3' icon={<Icon id={1} open={open} />}>
//                         <AccordionHeader onClick={() => handleOpen(1)}>Fees & Charges</AccordionHeader>
//                         <AccordionBody>
//                             <div className="flex flex-col md:flex-row items-center py-4 ml-4">
//                                 {/* Left Side - Headings */}
//                                 <div className="md:w-1/2 mb-4 md:mb-0">
//                                 {/* {cardDetails.Card_features[1].subfeatures.slice(0, 3).map((feature, index) => (
//                                     <div className="mb-2" key={index}>
//                                         <h2 className="text-lg font-semibold text-blue-600">{feature.Subfeatures}</h2>
//                                         <p className='text-gray-600 text-base'> {feature.description}</p>
//                                     </div>
//                                     ))} */}
//                                     {/* <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Rewards Redemption Fee</h2>
//                                         <p className='text-gray-600 text-base'>Nil</p>
//                                     </div>
//                                     <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Fuel Surcharge
//                                         </h2>
//                                         <p className='text-gray-600 text-base'>Enjoy a 1% waiver on fuel surcharge for transactions ranging from Rs. 400 to Rs. 5,000 at fuel stations.</p>
//                                     </div> */}
//                                 </div>
//  {/* Right Side - Headings */}
//                                 <div className="md:w-1/2 md:ml-6">
//                                 {/* {cardDetails.Card_features[1].subfeatures.slice(3).map((feature, index) => (

//                                     <div className="mb-2"key={index}>
//                                         <h2 className="text-lg font-semibold text-blue-600">{feature.Subfeatures}</h2>
//                                         <p className='text-gray-600 text-base'>{feature.description}</p>
//                                     </div>
//                                      ))} */}
//                                     {/* <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Waiver Based on Spending</h2>
//                                         <p className='text-gray-600 text-base' >N/A</p>
//                                     </div>
//                                     <div className="mb-2">
//                                         <h2 className="text-lg font-semibold text-blue-600">Add-on Card Fee</h2>
//                                         <p className='text-gray-600 text-base'>Nil</p>
//                                     </div> */}
//                                 </div>

//                             </div>

//                         </AccordionBody>
//                     </Accordion>
//                     <Accordion className='px-3' open={open === 2} icon={<Icon id={2} open={open} />}>

//                         <AccordionHeader onClick={() => handleOpen(2)}>
//                             Card Details
//                         </AccordionHeader>

//                         <AccordionBody>
//                         {/* {cardDetails.Card_features[2].subfeatures.map((feature, index) => (

//                         <p key={index} className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6">
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         {feature.description}
//                     </p>
//                     ))} */}

//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6">
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Enjoy a complimentary 3-month membership for Zee5, Amazon Prime, and Cult.fit with your card.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6">
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Earn 1 Reward Point for every Rs. 100 spent on retail transactions.
//                     </p>

//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6">
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Avail accelerated Reward Points at 10X or 5X rates for both online and offline transactions.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6">
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span>
//                         Benefit from a 1% surcharge waiver on fuel transactions ranging from Rs. 400 to Rs. 5,000
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Get 5% and 2% cashback on retail spends upon reaching the spend target.
// Receive an additional 5% cashback on travel, dining, and grocery expenses.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-base relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Enjoy 4 complimentary lounge accesses per quarter.
//                     </p>

//                         </AccordionBody>
//                     </Accordion>

//                 </div>

//                 <p className="text-gray-700 text-base mb-6" dangerouslySetInnerHTML={{ __html:cardDetails.Card_details }}></p>

//                 <div className=" mx-auto ">
//                 {/* {cardDetails.Card_details[2].subfeatures.map((feature, index) => ( */}
//                 <div className="text-xl md:text-2xl lg:text-4xl font-bold mt-8 mb-4 ml-4">AU Bank LIT Credit Card Detail</div>
//                 <div className="text-sm md:text-base lg:text-lg text-gray-600 mb-8">
//                     <p className=' px-4 mt-2   text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  '>

// The AU Small Finance LIT Credit Card stands out as India’s pioneer customizable credit card, allowing users to tailor their card's features to their preferences and pay only for what they choose. This innovation from AU Bank introduces a unique offering in the credit card landscape, unlike the multitude of options available from other providers. With a plethora of credit cards in the market, each boasting different features and benefits, customers often find themselves overwhelmed and end up holding multiple cards, each with its associated fees.

// </p>
// </div>

//                 <p className=' px-4 mt-2   text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg '>

//                     AU Bank identified this challenge and addressed it by launching the LIT credit card, the first-ever customizable credit card. While the bank offers several other cards with various benefits, the LIT card distinguishes itself with its customizable features.
//                 </p>
//                 <p className=' px-4 mt-2   text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg '>

//                     The LIT credit card empowers cardholders with the flexibility to select features that suit their needs for the next 90 days through the AU 0101 App, simplifying the process. Users can choose from a range of features, each offering unique benefits, including the opportunity to earn reward points. For every Rs. 100 spent on retail, users earn 1 Reward Point, with the option to enjoy accelerated Reward Points at 10X or 5X rates based on their selected feature. Additionally, the card provides enticing cashback benefits tailored to the chosen feature.
//                 </p>
//                 <p className=' px-4 mt-2   text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg '>

//                     Moreover, the LIT card extends complimentary memberships to premium brands like Zee5 and Amazon Prime, enhancing the cardholder experience. For frequent travelers, the card offers attractive cashback offers and complimentary access to airport lounges, adding further value.
//                 </p>
//                 <p className='px-4 mt-2   text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg  '>

//                     By selecting their desired feature, users can unlock a world of benefits and privileges tailored to their preferences.
//                 </p>
//             </div>
//                 <div className="container mx-auto px-4 py-8">
//                     <h1 className="text-2xl font-bold  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">Features and Benefits of AU Bank LIT Credit Card</h1>
//                     <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">The AU Small Finance Bank LIT Credit Card is India's pioneering customizable credit card, offering the unique advantage of selecting and paying for desired features exclusively. With a wide array of benefits spanning lifestyle, groceries, travel, and beyond, this card provides unparalleled flexibility and convenience.</p>
//                     <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">It provides a comprehensive range of credit card benefits and allows holders to select one or multiple features/categories simultaneously. This empowers customers to enjoy benefits from their chosen category while only paying for the features they use, rather than the entire annual card fees.</p>
//                 </div>

//                 <div className="container mx-auto px-4 py-8">
//                     <h1 className="text-2xl font-bold  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">Cashback & Reward Points</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6 mt-4"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0 mt-0.5 text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         This credit card offers the opportunity to earn both cashback and reward points on transactions, in addition to various other benefits. Here are some of the key features and advantages:
//                     </p>
//                     <h1 className="text-2xl font-bold pl-4 py-2 mt-2">CashBack :</h1>
//                     <h1 className="text-lg font-bold pl-4 py-2">Earn cashback of up to 5%.</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Receive 5% cashback three times within a 90-day period.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Get 5% cashback on retail purchases, alongside earning 1 Reward Point for every Rs. 100 spent.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Minimum retail expenditures of Rs. 7,500 with a maximum cashback limit of Rs. 500 within a 30-day timeframe.
//                     </p>

//                     <h1 className="text-lg font-bold pl-4 py-2 mt-2">Earn cashback of up to 2%.</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Receive 2% cashback three times within a 90-day period.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Get 2% cashback on retail purchases, alongside earning 1 Reward Point for every Rs. 100 spent.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Minimum retail expenditures of Rs. 10,000 with a maximum cashback limit of Rs. 1000 within a 30-day timeframe.
//                     </p>
//                     <h1 className="text-lg font-bold pl-4 py-2 mt-2">Category Cashback</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Receive an extra 5% cashback on travel, dining, grocery, electronics, and apparel purchases, along with earning 1 reward point for every Rs. 100 spent.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Maximum cashback limit of Rs. 1,000 within each 30-day period.
//                     </p>

//                     <h1 className="text-2xl font-bold pl-4 py-2 mt-2">Rewards :</h1>
//                     <h1 className="text-lg font-bold pl-4 py-2">For Online Spends</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         10x Reward Points: For Rs. 299 over 90 days, earn 10x reward points on your online spending. Note that rent, cash withdrawals, and fuel spending are not eligible for rewards.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         5x Reward Points: For Rs. 199 over 90 days, earn 5x reward points on your online spending. Similarly, rent, cash withdrawals, and fuel spending are not included in this offer.
//                     </p>
//                     <h1 className="text-lg font-bold pl-4 py-2">For Offline Spends</h1>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         10x Reward Points: For Rs. 299 over 90 days, earn 10x reward points on your offline spending. Rent, cash withdrawals, and fuel spending are excluded.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         5x Reward Points: For Rs. 199 over 90 days, earn 5x reward points on your offline spending. Again, rent, cash withdrawals, and fuel spending do not count toward this reward.
//                     </p>
//                     <div className="container mx-auto px-4 py-8">
//                     <h1 className="text-2xl font-bold  bg-blue-100 border-l-4 border-blue-600 pl-4 py-2 ">How To Apply For The AU Bank LIT Credit Card?</h1>
//                     <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
//                     You can apply for the AU Bank LIT Credit Card through both online and offline channels.
//                     </p>
//                      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Click the "Apply Now" button on this page.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         You'll be redirected to HDFC's credit card application page.
//                     </p>
//                     <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg relative pl-6"> {/* Added pl-6 for left padding */}
//                         <span className="absolute left-0 top-0  text-blue-600">&#8226;</span> {/* Adjusted mt and added margin */}
//                         Complete the required form to submit your application.
//                     </p>
//                     </div>

//                 </div>
//             </div>
//             <div className="max-w-4xl mx-auto py-6">
//                         <h1 className="text-3xl font-semibold text-center mb-8">Frequently Asked Questions</h1>
//                         <div className="space-y-4">
//                             {cardDetails.card_faq.map((faq, index) => (
//                                 <div key={index} className="border rounded-lg overflow-hidden shadow-md">
//                                     <button
//                                         onClick={() => toggleAccordion(index)}
//                                         className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
//                                     >
//                                         {faq.question}
//                                         <svg
//                                             className={`w-6 h-6 transition-transform transform ${
//                                                 activeIndex === index ? 'rotate-180' : ''
//                                             }`}
//                                             fill="none"
//                                             viewBox="0 0 24 24"
//                                             stroke="currentColor"
//                                         >
//                                             <path
//                                                 strokeLinecap="round"
//                                                 strokeLinejoin="round"
//                                                 strokeWidth="2"
//                                                 d={activeIndex === index ? 'M19 9l-7 7-7-7' : 'M9 5l7 7 7-7'}
//                                             />
//                                         </svg>
//                                     </button>
//                                     {activeIndex === index && (
//                                         <div className="p-4 bg-gray-100">
//                                             <p className="text-gray-600">{faq.answer}</p>
//                                         </div>
//                                     )}
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//             {/* <div className="max-w-4xl mx-auto py-6">
//             <h1 className="text-3xl font-semibold text-center mb-8">Frequently Asked Questions</h1>
//             <div className="space-y-4">
//                 {faqs.map((faq, index) => (
//                     <div key={index} className="border rounded-lg overflow-hidden shadow-md">
//                         <button
//                             onClick={() => toggleAccordion(index)}
//                             className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
//                         >
//                             {faq.question}
//                             <svg
//                                 className={`w-6 h-6 transition-transform transform ${
//                                     activeIndex === index ? 'rotate-180' : ''
//                                 }`}
//                                 fill="none"
//                                 viewBox="0 0 24 24"
//                                 stroke="currentColor"
//                             >
//                                 <path
//                                     strokeLinecap="round"
//                                     strokeLinejoin="round"
//                                     strokeWidth="2"
//                                     d={activeIndex === index ? 'M19 9l-7 7-7-7' : 'M9 5l7 7 7-7'}
//                                 />
//                             </svg>
//                         </button>
//                         {activeIndex === index && (
//                             <div className="p-4 bg-gray-100">
//                                 <p className="text-gray-600">{faq.answer}</p>
//                             </div>
//                         )}
//                     </div>
//                 ))}
//             </div>
//         </div> */}

//         </>

//     );
// }

// export default LearnMoreAllCards;
